import React from 'react';
import { Services } from 'services';
import { Box } from '@clariness/box';
import { Text } from '@clariness/clarikit-react';
import { ShouldRender } from 'components/ShouldRender';
import { BoxLoader } from 'components/loaders';
import { formatDate, displayContentValue } from 'utils/format';
import { FormattedMessage, useIntl } from 'react-intl';
import { ServiceError } from 'components/ServiceError';
import { ReferralActivityDto } from 'common/api';
import { ReferralActivity } from 'global/referralActivity';
import { usePatientDetailsContext } from '../../ReferralDetails';
import { displayActivityEvent } from './displayActivityEvent';

export const PatientDetailsActivity = () => {
  const { currentReferral, isExternalReferral } = usePatientDetailsContext();

  const { activity, isLoading, isError, refetch } =
    Services.Referrals.useGetReferralActivity(currentReferral?.id, {
      isExternalReferral,
    });

  const intl = useIntl();

  if (isError) {
    return (
      <ServiceError onRetry={refetch}>
        <FormattedMessage
          id="is.patient_details_modal.activity.generic_error_message"
          defaultMessage="Something went wrong while we tried to display the patient's activity."
        />
      </ServiceError>
    );
  }

  const getActivityEventCreatorName = (activityEvent: ReferralActivityDto) => {
    if (activityEvent.creatorEmail) {
      return displayContentValue(activityEvent.creatorEmail);
    }
    if (!activityEvent.createdBy?.startsWith('auth0')) {
      return displayContentValue(activityEvent.createdBy?.split('|')[1]);
    }
    return 'NA';
  };

  const unescapeSpecialCharacters = (inputString: string): string =>
    inputString?.replace(/\\/g, '');

  return (
    <React.Fragment>
      <Box
        as="table"
        sx={{
          width: '100%',
          height: 'fit-content',

          '& th': {
            textAlign: 'left',
          },

          '& th:not(:last-child), td:not(:last-child)': {},

          '& td': {
            pt: '10px',
            pb: '16px',
            color: '#535A5F',
          },
        }}
      >
        <Box as="tbody">
          {activity?.referralActivities
            ?.filter(
              (activityEvent) =>
                activityEvent.event !== ReferralActivity.SCREENER_PAUSED &&
                activityEvent.event !==
                  ReferralActivityDto.event.PATIENT_SUMMARY
            )
            ?.map((activityEvent) => (
              <tr key={activityEvent.id}>
                <td
                  width="125px"
                  style={{
                    alignContent: 'baseline',
                  }}
                >
                  <Text as="p" variant="bodyMd" fontWeight="bold">
                    {formatDate(
                      intl.formatDate(
                        activityEvent.createdAt &&
                          new Date(activityEvent.createdAt)
                      )
                    )}
                  </Text>
                </td>

                <td>
                  <Text as="p" variant="bodyMd">
                    {displayActivityEvent(activityEvent.event, {
                      note: unescapeSpecialCharacters(activityEvent.note || ''),
                      referralStatus: activityEvent.referralStatus,
                      scheduledDate: activityEvent.appointmentDateTime,
                      timeZone: activity.timeZone,
                    })}
                  </Text>
                </td>

                <td
                  style={{
                    textAlign: 'right',
                  }}
                >
                  <Text as="p" variant="bodyMd">
                    {getActivityEventCreatorName(activityEvent)}
                  </Text>
                </td>
              </tr>
            ))}
        </Box>
      </Box>

      <ShouldRender when={isLoading}>
        <BoxLoader />
      </ShouldRender>
    </React.Fragment>
  );
};
